import { emit } from './emit'
import { ensurePageViewId } from './pageViewId'

/** Landing-page load latency telemetry. Scoped to paid-traffic entry
 *  points only (SKU detail + `/ssr/*` SEO landings). Times come from
 *  PerformanceNavigationTiming so they reflect what the user experienced. */

export type LandingPageId = 'sku_detail' | 'ssr_urgent_care' | 'lilly_weight_loss' | (string & {})

interface NavigationTimings {
    ttfbMs: number | null
    dclMs: number | null
    loadEventMs: number | null
    navigationType: 'navigate' | 'reload' | 'back_forward' | 'prerender' | null
    cacheHit: boolean | null
}

function readNavigationTimings(): NavigationTimings {
    if (typeof performance === 'undefined') {
        return { ttfbMs: null, dclMs: null, loadEventMs: null, navigationType: null, cacheHit: null }
    }

    // Prefer the modern PerformanceNavigationTiming entry.
    const navEntries = performance.getEntriesByType?.('navigation') as PerformanceNavigationTiming[] | undefined
    const nav = navEntries?.[0]

    if (nav) {
        const ttfbMs = Math.max(0, Math.round(nav.responseStart - nav.startTime))
        const dclMs = Math.max(0, Math.round(nav.domContentLoadedEventEnd - nav.startTime))
        const loadEventMs =
            nav.loadEventEnd > 0 ? Math.max(0, Math.round(nav.loadEventEnd - nav.startTime)) : null
        // transferSize === 0 with a non-zero encodedBodySize is a 304/disk-cache signal.
        const cacheHit = nav.transferSize === 0 && nav.encodedBodySize > 0
        return {
            ttfbMs,
            dclMs,
            loadEventMs,
            navigationType: nav.type as NavigationTimings['navigationType'],
            cacheHit,
        }
    }

    // Legacy fallback (Safari < 15.4 still ships this).
    const t = (performance as unknown as { timing?: PerformanceTiming }).timing
    if (t && t.navigationStart > 0) {
        const ttfbMs = t.responseStart > 0 ? Math.max(0, t.responseStart - t.navigationStart) : null
        const dclMs =
            t.domContentLoadedEventEnd > 0 ? Math.max(0, t.domContentLoadedEventEnd - t.navigationStart) : null
        const loadEventMs = t.loadEventEnd > 0 ? Math.max(0, t.loadEventEnd - t.navigationStart) : null
        return { ttfbMs, dclMs, loadEventMs, navigationType: null, cacheHit: null }
    }

    return { ttfbMs: null, dclMs: null, loadEventMs: null, navigationType: null, cacheHit: null }
}

function readPaintMs(name: 'first-contentful-paint'): number | null {
    if (typeof performance === 'undefined') return null
    const entry = performance.getEntriesByType?.('paint').find((e) => e.name === name)
    return entry ? Math.round(entry.startTime) : null
}

function readLcpMs(): number | null {
    if (typeof performance === 'undefined') return null
    const lcpEntries = performance.getEntriesByType?.('largest-contentful-paint') as
        | (PerformanceEntry & { renderTime?: number; loadTime?: number })[]
        | undefined
    if (!lcpEntries || lcpEntries.length === 0) return null
    const last = lcpEntries[lcpEntries.length - 1]
    return Math.round(last.renderTime || last.loadTime || last.startTime)
}

interface TrackArgs {
    pageId: LandingPageId
    skuId?: string | null
    isAuthed: boolean
}

/** Per-pageview dedup that survives React 18 strict-mode remounts. */
const emittedForPageView = new Set<string>()

export function _resetLandingPageLatencyDedupForTests(): void {
    emittedForPageView.clear()
}

export function trackLandingPageLoaded(args: TrackArgs): void {
    const pageViewId = ensurePageViewId()
    if (emittedForPageView.has(pageViewId)) return
    emittedForPageView.add(pageViewId)

    const timings = readNavigationTimings()
    const fcpMs = readPaintMs('first-contentful-paint')
    const lcpMs = readLcpMs()

    // No `path` / `sku_hyphenated_name`: warehouse joins sku_id to the
    // SKUs table to recover slug. Keeps the medical-service slug out of
    // the payload Segment auto-decorates with userId post-identify.
    emit('landing_page_loaded', {
        page_view_id: pageViewId,
        page_id: args.pageId,
        sku_id: args.skuId ?? null,
        ttfb_ms: timings.ttfbMs,
        dcl_ms: timings.dclMs,
        load_event_ms: timings.loadEventMs,
        fcp_ms: fcpMs,
        lcp_ms: lcpMs,
        navigation_type: timings.navigationType,
        cache_hit: timings.cacheHit,
        is_authed: args.isAuthed,
    })
}
